import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CheckSquare, Plus, List, LayoutList, LayoutGrid, RefreshCw, ArrowUp } from 'lucide-react'
import { clsx } from 'clsx'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { springGentle } from '@/lib/motionConfig'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { format, startOfWeek, endOfWeek } from 'date-fns'
import { parseNaturalLanguage } from '@/lib/nlParser'
import { PageContainer } from '@/components/layout/PageContainer'
import { CategoryTabs } from './CategoryTabs'
import { QuickFilters, type QuickFilterType } from './QuickFilters'
import { TaskItem } from './TaskItem'
import { TaskEmptyState } from './TaskEmptyState'
import { TaskListSkeleton } from './TaskListSkeleton'
import { TaskDashboardHeader } from './TaskDashboardHeader'
import { NlHintChips } from './NlHintChips'
import { TaskCreateModal } from './TaskCreateModal'
import { TaskDetailPanel } from './TaskDetailPanel'
import { TaskGroupSection } from './TaskGroup'
import { BatchActionBar } from './BatchActionBar'
import { PrintButton } from '@/components/ui/PrintButton'
import { Tooltip } from '@/components/ui/Tooltip'
import { useTaskFilters } from '@/hooks/useTaskFilters'
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import { groupTasksByDate, getSharedTaskGroupKey, GROUP_ORDER, GROUP_META } from '@/lib/taskGrouping'
import { useTaskStore } from '@/stores/taskStore'
import { useCategoryStore } from '@/stores/categoryStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useUIStore } from '@/stores/uiStore'
import { useShallow } from 'zustand/react/shallow'
import { useAuthStore } from '@/stores/authStore'
import { useGroupStore } from '@/stores/groupStore'
import { useMySharedTasks } from '@/hooks/useMySharedTasks'
import { useSharedGroupStore } from '@/stores/sharedGroupStore'
import { SharedTaskInlineItem } from './SharedTaskInlineItem'
import { SharedTaskDetailPanel } from './SharedTaskDetailPanel'
import { AuxPanel } from '@/components/layout/AuxPanel'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import type { UnifiedTask } from '@/lib/sharedTypes'
import { flattenTaskGroups, type FlatItem } from '@/lib/taskGrouping'
import type { Task, TaskViewMode } from '@/lib/types'

type SourceFilter = 'all' | 'personal' | 'shared'

type MergedItem =
  | { kind: 'personal'; task: Task }
  | { kind: 'shared'; task: UnifiedTask }

interface MergedGroup {
  key: string
  label: string
  color?: string
  items: MergedItem[]
}

const viewModeIcons: Record<TaskViewMode, typeof List> = {
  compact: List,
  standard: LayoutList,
  card: LayoutGrid,
}

const viewModeLabels: Record<TaskViewMode, string> = {
  compact: '컴팩트',
  standard: '기본',
  card: '카드',
}

const viewModeOrder: TaskViewMode[] = ['compact', 'standard', 'card']

export function TaskList() {
  const { tasks, toggleComplete, toggleFlag, deleteTask, reorderTasks, addTask, refreshFromDb, isHydrated } = useTaskStore(
    useShallow((s) => ({
      tasks: s.tasks,
      toggleComplete: s.toggleComplete,
      toggleFlag: s.toggleFlag,
      deleteTask: s.deleteTask,
      reorderTasks: s.reorderTasks,
      addTask: s.addTask,
      refreshFromDb: s.refreshFromDb,
      isHydrated: s.isHydrated,
    }))
  )
  const categories = useCategoryStore((s) => s.categories)
  const { isSelectionMode, toggleSelectionMode, toggleTaskSelection, selectedTaskIds, openTaskEditModal, activeCategoryId, setActiveCategoryId } = useUIStore(
    useShallow((s) => ({
      isSelectionMode: s.isSelectionMode,
      toggleSelectionMode: s.toggleSelectionMode,
      toggleTaskSelection: s.toggleTaskSelection,
      selectedTaskIds: s.selectedTaskIds,
      openTaskEditModal: s.openTaskEditModal,
      activeCategoryId: s.activeCategoryTab,
      setActiveCategoryId: s.setActiveCategoryTab,
    }))
  )
  const syncStatus = useAuthStore((s) => s.syncStatus)
  const groups = useGroupStore((s) => s.groups)
  const viewMode = useSettingsStore((s) => s.settings.viewMode)
  const setViewMode = useSettingsStore((s) => s.setViewMode)
  const mySharedTasks = useMySharedTasks()
  const toggleSharedTask = useSharedGroupStore((s) => s.toggleTask)
  const isLoggedIn = useAuthStore((s) => !!s.user)

  const [quickFilter, setQuickFilter] = useState<QuickFilterType>('all')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [quickAddText, setQuickAddText] = useState('')
  const quickAddRef = useRef<HTMLInputElement>(null)
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [selectedSharedTask, _setSelectedSharedTask] = useState<UnifiedTask | null>(null)
  const setAuxPanelGroupId = useUIStore((s) => s.setAuxPanelGroupId)
  const isAuxPanelOpen = useUIStore((s) => s.isAuxPanelOpen)
  const setSelectedSharedTask = useCallback((task: UnifiedTask | null) => {
    _setSelectedSharedTask(task)
    setAuxPanelGroupId(task?.groupId || null)
    if (task) setSelectedTaskId(null)
  }, [setAuxPanelGroupId])
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  )
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)')
    const handleChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches)
    setIsDesktop(media.matches)
    media.addEventListener('change', handleChange)
    return () => media.removeEventListener('change', handleChange)
  }, [])

  // Sync URL query params → filters (category, starred)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const categoryParam = params.get('category')
    const filterParam = params.get('filter')

    if (categoryParam) {
      const catId = parseInt(categoryParam, 10)
      if (!isNaN(catId)) setActiveCategoryId(catId)
    }
    if (filterParam === 'starred') {
      setQuickFilter('flagged')
    }
  }, [location.search, setActiveCategoryId])

  // Pull-to-refresh
  const { pullDistance, isRefreshing, thresholdReached, threshold: pullThreshold, handlers: pullHandlers } = usePullToRefresh({
    onRefresh: refreshFromDb,
    threshold: 60,
  })
  const shouldReduceMotion = useReducedMotion()

  // Delay-based activation: allows swipe gestures without triggering drag
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Filter tasks by selected category
  const categoryFiltered = useTaskFilters(tasks, {
    categoryId: activeCategoryId ?? undefined,
    categories,
  })

  // Quick filter (including preset smart filters)
  const filteredTasks = useMemo(() => {
    if (quickFilter === 'all') return categoryFiltered
    const today = format(new Date(), 'yyyy-MM-dd')
    const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
    const weekEnd = format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')

    switch (quickFilter) {
      case 'today':
        return categoryFiltered.filter((t) => t.dueDate === today)
      case 'week':
        return categoryFiltered.filter((t) => t.dueDate && t.dueDate >= weekStart && t.dueDate <= weekEnd)
      case 'flagged':
        return categoryFiltered.filter((t) => t.isFlagged)
      case 'overdue':
        return categoryFiltered.filter((t) => t.status === 'pending' && t.dueDate && t.dueDate < today)
      case 'high-week':
        return categoryFiltered.filter(
          (t) => t.status === 'pending' && t.priority === 'high' && t.dueDate && t.dueDate >= weekStart && t.dueDate <= weekEnd
        )
      case 'recent-done':
        return tasks.filter(
          (t) => t.status === 'completed' && t.completedAt && t.completedAt >= weekStart
        )
      default:
        return categoryFiltered
    }
  }, [categoryFiltered, tasks, quickFilter])

  // Shared tasks filtered by quick filter + source filter → 날짜 그룹별 분류까지 한번에
  const { filteredSharedTasks, sharedTasksByGroupKey } = useMemo(() => {
    if (sourceFilter === 'personal' || mySharedTasks.length === 0) {
      return { filteredSharedTasks: [] as typeof mySharedTasks, sharedTasksByGroupKey: {} as Record<string, typeof mySharedTasks> }
    }
    let result = mySharedTasks
    if (quickFilter !== 'all') {
      const today = format(new Date(), 'yyyy-MM-dd')
      const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
      const weekEnd = format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
      switch (quickFilter) {
        case 'today':
          result = result.filter((t) => t.dueDate === today)
          break
        case 'week':
          result = result.filter((t) => t.dueDate && t.dueDate >= weekStart && t.dueDate <= weekEnd)
          break
        case 'flagged':
          result = []
          break
        case 'overdue':
          result = result.filter((t) => t.status === 'pending' && t.dueDate && t.dueDate < today)
          break
        case 'high-week':
          result = result.filter(
            (t) => t.status === 'pending' && t.priority === 'high' && t.dueDate && t.dueDate >= weekStart && t.dueDate <= weekEnd
          )
          break
        case 'recent-done':
          result = result.filter((t) => t.status === 'completed')
          break
      }
    }
    // 날짜 그룹별로 분류
    const grouped: Record<string, typeof mySharedTasks> = {}
    for (const st of result) {
      const key = getSharedTaskGroupKey(st.status, st.dueDate)
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(st)
    }
    return { filteredSharedTasks: result, sharedTasksByGroupKey: grouped }
  }, [mySharedTasks, quickFilter, sourceFilter])

  // Source filter 적용된 개인 태스크
  const personalFilteredTasks = useMemo(() => {
    if (sourceFilter === 'shared') return []
    return filteredTasks
  }, [filteredTasks, sourceFilter])

  // Smart date grouping
  const taskGroups = useMemo(() => groupTasksByDate(personalFilteredTasks), [personalFilteredTasks])

  // 통합 뷰: sourceFilter === 'all' && 공유 태스크 존재 시 개인+공유를 마감일순으로 통합
  const mergedGroups = useMemo<MergedGroup[]>(() => {
    if (sourceFilter !== 'all' || filteredSharedTasks.length === 0) return []

    const buckets = new Map<string, MergedItem[]>()

    for (const task of personalFilteredTasks) {
      const key = getSharedTaskGroupKey(task.status, task.dueDate)
      const arr = buckets.get(key) ?? []
      arr.push({ kind: 'personal', task })
      buckets.set(key, arr)
    }

    for (const st of filteredSharedTasks) {
      const key = getSharedTaskGroupKey(st.status, st.dueDate)
      const arr = buckets.get(key) ?? []
      arr.push({ kind: 'shared', task: st })
      buckets.set(key, arr)
    }

    // 각 그룹 내에서 마감일 → 마감시간 순 정렬
    for (const [, items] of buckets) {
      items.sort((a, b) => {
        const aDate = a.kind === 'personal' ? (a.task.dueDate ?? '') : (a.task.dueDate ?? '')
        const bDate = b.kind === 'personal' ? (b.task.dueDate ?? '') : (b.task.dueDate ?? '')
        if (aDate !== bDate) return (aDate || '9999').localeCompare(bDate || '9999')
        const aTime = a.kind === 'personal' ? (a.task.dueTime ?? '') : (a.task.dueTime ?? '')
        const bTime = b.kind === 'personal' ? (b.task.dueTime ?? '') : (b.task.dueTime ?? '')
        return aTime.localeCompare(bTime)
      })
    }

    const result: MergedGroup[] = []
    for (const key of GROUP_ORDER) {
      const items = buckets.get(key)
      if (items && items.length > 0) {
        const meta = GROUP_META[key as keyof typeof GROUP_META]
        result.push({ key, label: meta?.label ?? key, color: meta?.color, items })
      }
    }
    return result
  }, [personalFilteredTasks, filteredSharedTasks, sourceFilter])

  // Task-to-group mapping for badges
  const taskGroupMap = useMemo(() => {
    const map = new Map<number, { name: string; color: string }[]>()
    for (const g of groups) {
      for (const taskId of g.taskIds) {
        const existing = map.get(taskId) || []
        existing.push({ name: g.name, color: g.color })
        map.set(taskId, existing)
      }
    }
    return map
  }, [groups])

  const getCategoryInfo = (categoryId: number | null) => {
    if (categoryId === null) return { name: undefined, color: undefined }
    const cat = categories.find((c) => c.id === categoryId)
    return { name: cat?.name, color: cat?.color }
  }

  const handleTaskClick = useCallback((id: number) => {
    if (isSelectionMode) {
      toggleTaskSelection(id)
      return
    }
    // Desktop: show detail panel instead of modal
    if (isDesktop) {
      setSelectedTaskId(id)
      setSelectedSharedTask(null)
      return
    }
    // Mobile: navigate to the unified task detail route (flows#3)
    navigate(`/task/${id}`)
  }, [isSelectionMode, toggleTaskSelection, navigate, setSelectedSharedTask, isDesktop])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    reorderTasks(Number(active.id), Number(over.id))
  }

  // Delete wrapper: clear detail panel if deleted task was shown
  const handleDeleteTask = useCallback((id: number) => {
    deleteTask(id)
    setSelectedTaskId((prev) => (prev === id ? null : prev))
  }, [deleteTask])

  // Live NL parse preview for the inline quick-add (chips appear before Enter)
  const quickAddParsed = useMemo(() => {
    const raw = quickAddText.trim()
    if (!raw) return null
    const result = parseNaturalLanguage(raw)
    if (!result.dueDate && !result.dueTime && !result.priority) return null
    return result
  }, [quickAddText])

  // Inline quick add with NL parsing
  const handleQuickAdd = useCallback(async () => {
    const raw = quickAddText.trim()
    if (!raw) return
    const { cleanTitle, dueDate, dueTime, priority } = parseNaturalLanguage(raw)
    const now = new Date().toISOString()
    await addTask({
      title: cleanTitle,
      categoryId: activeCategoryId,
      dueDate,
      dueTime,
      status: 'pending',
      priority: priority || 'none',
      isFlagged: false,
      isStarred: false,
      alarm: { enabled: false },
      repeat: { type: 'none', interval: 1 },
      subtasks: [],
      createdAt: now,
      updatedAt: now,
      sortOrder: 0,
    })
    setQuickAddText('')
    quickAddRef.current?.focus()
  }, [quickAddText, activeCategoryId, addTask])

  // Cycle view mode
  const cycleViewMode = () => {
    const idx = viewModeOrder.indexOf(viewMode)
    const next = viewModeOrder[(idx + 1) % viewModeOrder.length]
    setViewMode(next)
  }

  const ViewIcon = viewModeIcons[viewMode]

  // Pending tasks for keyboard navigation
  const pendingTasks = useMemo(() => filteredTasks.filter(t => t.status === 'pending'), [filteredTasks])
  const focusedTaskId = focusedIndex >= 0 && focusedIndex < pendingTasks.length ? pendingTasks[focusedIndex].id : null

  // Keyboard navigation for task list
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

      const items = pendingTasks
      if (items.length === 0) return

      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault()
          setFocusedIndex(prev => Math.min(prev + 1, items.length - 1))
          break
        case 'k':
        case 'ArrowUp':
          e.preventDefault()
          setFocusedIndex(prev => Math.max(prev - 1, 0))
          break
        case 'Enter':
          if (focusedIndex >= 0 && focusedIndex < items.length) {
            e.preventDefault()
            handleTaskClick(items[focusedIndex].id as number)
          }
          break
        case ' ':
          if (focusedIndex >= 0 && focusedIndex < items.length) {
            e.preventDefault()
            toggleComplete(items[focusedIndex].id as number)
          }
          break
        case 'Delete':
        case 'Backspace':
          if (focusedIndex >= 0 && focusedIndex < items.length) {
            e.preventDefault()
            const task = items[focusedIndex]
            if (window.confirm(`"${task.title}" 작업을 삭제하시겠습니까?`)) {
              handleDeleteTask(task.id as number)
              setFocusedIndex(prev => Math.min(prev, items.length - 2))
            }
          }
          break
        case 'e':
          if (focusedIndex >= 0 && focusedIndex < items.length) {
            e.preventDefault()
            openTaskEditModal(items[focusedIndex])
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [pendingTasks, focusedIndex, handleTaskClick, toggleComplete, handleDeleteTask, openTaskEditModal])

  // Reset keyboard focus when filter changes
  useEffect(() => {
    setFocusedIndex(-1)
  }, [activeCategoryId, quickFilter])

  const isEmpty = personalFilteredTasks.length === 0 && filteredSharedTasks.length === 0
  const sortableIds = personalFilteredTasks.filter((t) => t.status === 'pending').map((t) => t.id!)

  // Virtual scrolling: 50+ personal tasks이고 통합 뷰가 아닐 때만 활성화
  // (통합 뷰에서는 mergedGroups가 올바른 순서를 보장하므로 virtual 불필요)
  const useVirtual = personalFilteredTasks.length > 50 && mergedGroups.length === 0

  const virtualRows = useMemo<FlatItem[]>(() => {
    if (!useVirtual) return []
    return flattenTaskGroups(taskGroups)
  }, [useVirtual, taskGroups])

  const getEstimateSize = useCallback((index: number) => {
    if (!useVirtual) return 72
    const row = virtualRows[index]
    if (row.type === 'header') return 48
    return viewMode === 'compact' ? 52 : viewMode === 'card' ? 120 : 72
  }, [useVirtual, virtualRows, viewMode])

  const virtualGap = viewMode === 'compact' ? 8 : viewMode === 'card' ? 16 : 12

  const virtualizer = useWindowVirtualizer({
    count: useVirtual ? virtualRows.length : 0,
    estimateSize: getEstimateSize,
    overscan: 10,
    enabled: useVirtual,
    gap: virtualGap,
  })

  return (
    <PageContainer>
      <div className="lg:flex lg:gap-6 lg:h-[calc(100dvh-128px)]">
      <div className="flex-1 min-w-0 lg:overflow-y-auto lg:pr-1">
      {/* Pull-to-refresh indicator (motion#7: 진행도 연동 + 스프링 스냅백) */}
      {/* reduced-motion: 콘텐츠를 밀어내지 않고 opacity만 변하도록 y 고정 */}
      <motion.div
        {...pullHandlers}
        animate={{ y: shouldReduceMotion ? 0 : pullDistance }}
        transition={pullDistance > 0 ? { duration: 0 } : springGentle}
      >
      {pullDistance > 0 && (() => {
        // 당김 진행도(0~1+). 임계점 기준으로 회전/스케일/페이드를 연동한다.
        const progress = pullDistance / pullThreshold
        const opacity = Math.min(progress, 1)
        const showRelease = thresholdReached || isRefreshing
        // reduced-motion: 회전/스케일 비활성, opacity만
        const iconAnimate = shouldReduceMotion
          ? { opacity }
          : {
              opacity,
              rotate: isRefreshing ? 0 : progress * 270,
              scale: 0.6 + Math.min(progress, 1) * 0.4 + (showRelease && !isRefreshing ? 0.15 : 0),
            }
        return (
          <div className="flex justify-center -mt-2 mb-2">
            <motion.div
              className="text-primary-500"
              animate={iconAnimate}
              transition={shouldReduceMotion ? { duration: 0.15 } : springGentle}
            >
              {showRelease && !isRefreshing ? (
                <ArrowUp className="w-6 h-6" strokeWidth={1.6} aria-label="놓으면 새로고침" />
              ) : (
                <RefreshCw className={clsx('w-6 h-6', isRefreshing && 'animate-spin')} strokeWidth={1.6} aria-label={isRefreshing ? '새로고침 중' : '당겨서 새로고침'} />
              )}
            </motion.div>
          </div>
        )
      })()}
      <TaskDashboardHeader tasks={tasks} onCreateTask={() => setIsCreateOpen(true)} />

      <section className="workspace-panel mb-5 p-3 sm:p-4" aria-label="작업 필터 및 빠른 추가">
      {/* Category tabs */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <CategoryTabs
            categories={categories}
            activeCategoryId={activeCategoryId}
            onSelect={setActiveCategoryId}
          />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <PrintButton />
          <Tooltip content={viewModeLabels[viewMode]} placement="bottom">
            <button
              type="button"
              onClick={cycleViewMode}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-disabled hover:text-[var(--text-secondary)] hover-surface transition-colors"
              aria-label={`뷰 모드: ${viewModeLabels[viewMode]}`}
            >
              <ViewIcon className="w-5 h-5" />
            </button>
          </Tooltip>
          <Tooltip content="선택 모드" placement="bottom">
            <button
              type="button"
              onClick={toggleSelectionMode}
              className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl transition-colors ${isSelectionMode
                ? 'text-accent-primary bg-accent-primary'
                : 'text-disabled hover:text-[var(--text-secondary)] hover-surface'
                }`}
              aria-label="선택 모드"
            >
              <CheckSquare className="w-5 h-5" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Quick filters */}
      <div className="mt-3 flex flex-col items-stretch gap-2.5 sm:flex-row sm:items-center sm:gap-3">
        <QuickFilters active={quickFilter} onChange={setQuickFilter} />

        {/* Source filter: 전체/개인/공유 */}
        {isLoggedIn && mySharedTasks.length > 0 && (
          <div className="flex items-center gap-1 flex-shrink-0 sm:ml-auto">
            {([
              { key: 'all' as SourceFilter, label: '전체' },
              { key: 'personal' as SourceFilter, label: '개인' },
              { key: 'shared' as SourceFilter, label: '공유' },
            ]).map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSourceFilter(opt.key)}
                className={clsx(
                  'min-h-[44px] px-3 text-meta font-medium rounded-xl transition-colors',
                  sourceFilter === opt.key
                    ? 'bg-primary-500 text-white'
                    : 'text-caption hover:bg-surface-tertiary'
                )}
              >
                {opt.label}
                {opt.key === 'shared' && (
                  <span className="ml-0.5">({mySharedTasks.filter(t => t.status === 'pending').length})</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Inline quick add */}
      <div className="mt-3 flex items-start gap-2.5">
        <div className="flex-1">
          <div className="relative">
            <input
              ref={quickAddRef}
              type="text"
              value={quickAddText}
              onChange={(e) => setQuickAddText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleQuickAdd()
                }
              }}
              placeholder="예: 내일 오후 3시 회의 긴급"
              className="min-h-[52px] w-full rounded-2xl border border-[var(--workspace-border-strong)] bg-[var(--workspace-panel-strong)] px-4 pr-14 text-sm text-heading shadow-[inset_0_1px_2px_rgba(15,23,42,0.03)] placeholder:text-caption focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
            {quickAddText.trim() && (
              <button
                type="button"
                onClick={handleQuickAdd}
                className="workspace-primary-action absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl text-white transition-all active:scale-95"
                aria-label="추가"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* 자연어 파싱 미리보기 — 입력 즉시 추출 항목 표시 */}
          <NlHintChips parsed={quickAddParsed} className="mt-2 px-1" />
        </div>
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          className="min-h-[52px] flex-shrink-0 rounded-2xl border border-[var(--workspace-border-strong)] bg-[var(--workspace-panel-strong)] px-4 text-sm font-medium text-body hover-surface transition-colors"
          aria-label="상세 추가"
        >
          상세
        </button>
      </div>
      </section>

      {/* Sync progress bar */}
      {syncStatus === 'syncing' && (
        <div className="mt-3 mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-disabled">
              동기화 중...
            </span>
          </div>
          <div className="h-1 bg-surface-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 rounded-full w-full animate-pulse" />
          </div>
        </div>
      )}

      {/* 스켈레톤 → 콘텐츠 크로스페이드 (states#1 + motion#6) */}
      <AnimatePresence mode="wait" initial={false}>
      {!isHydrated && tasks.length === 0 ? (
        <motion.div
          key="task-skeleton"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <TaskListSkeleton />
        </motion.div>
      ) : (
        <motion.div
          key="task-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
      {isEmpty ? (
        <TaskEmptyState onCreateTask={() => setIsCreateOpen(true)} />
      ) : useVirtual ? (
        /* Virtual scrolling mode (50+ tasks) — no DnD */
        <>
        <div
          style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
          className="mt-1"
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = virtualRows[virtualRow.index]
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {row.type === 'header' ? (
                  <div className="flex items-center gap-2 px-1 py-2">
                    <span className={`text-sm font-semibold ${row.color || 'text-body'}`}>{row.label}</span>
                    <span className="text-xs text-disabled">{row.count}</span>
                  </div>
                ) : (
                  <TaskItem
                    task={row.task}
                    categoryName={getCategoryInfo(row.task.categoryId).name}
                    categoryColor={getCategoryInfo(row.task.categoryId).color}
                    onToggleComplete={toggleComplete}
                    onToggleFlag={toggleFlag}
                    onClick={handleTaskClick}
                    isSelectionMode={isSelectionMode}
                    isSelected={selectedTaskIds.has(row.task.id as number)}
                    onSelect={toggleTaskSelection}

                    onDelete={handleDeleteTask}
                    sortable={false}
                    taskGroups={taskGroupMap.get(row.task.id as number)}
                    onEnterSelectionMode={toggleSelectionMode}
                    viewMode={viewMode}
                    activeCategoryId={activeCategoryId}
                    disableRowMotion
                  />
                )}
              </div>
            )
          })}
        </div>
        {/* Virtual 모드에서도 공유 태스크 렌더링 */}
        {Object.keys(sharedTasksByGroupKey).length > 0 && (
          <div className="space-y-4 mt-1">
            {(GROUP_ORDER as readonly string[])
              .filter((k) => sharedTasksByGroupKey[k]?.length > 0)
              .map((key) => {
                const sharedTasks = sharedTasksByGroupKey[key]
                const meta = GROUP_META[key as keyof typeof GROUP_META] || { label: key }
                return (
                  <TaskGroupSection
                    key={`shared-v-${key}`}
                    label={meta.label}
                    count={sharedTasks.length}
                    color={meta.color}
                    defaultOpen={key !== 'completed'}
                    viewMode={viewMode}
                  >
                    {sharedTasks.map((st) => (
                      <SharedTaskInlineItem
                        key={st.id}
                        task={st}
                        onToggle={() => {
                          if (st.groupId && st.sharedTask) {
                            toggleSharedTask(st.groupId, st.sharedTask.id, st.sharedTask.status)
                          }
                        }}
                        onClick={() => {
                          setSelectedSharedTask(st)
                        }}
                      />
                    ))}
                  </TaskGroupSection>
                )
              })}
          </div>
        )}
        </>
      ) : (
        /* Standard rendering (≤50 tasks) — with DnD */
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-4 mt-1">
              {(() => {
                // ── 통합 뷰: 개인+공유 마감일순 통합 렌더링 ──
                if (mergedGroups.length > 0) {
                  return mergedGroups.map((group) => (
                    <TaskGroupSection
                      key={group.key}
                      label={group.label}
                      count={group.items.length}
                      color={group.color}
                      defaultOpen={group.key !== 'completed'}
                      viewMode={viewMode}
                    >
                      <AnimatePresence mode="popLayout" initial={false}>
                      {group.items.map((item) => {
                        if (item.kind === 'personal') {
                          const { name, color } = getCategoryInfo(item.task.categoryId)
                          return (
                            <TaskItem
                              key={item.task.id}
                              task={item.task}
                              categoryName={name}
                              categoryColor={color}
                              onToggleComplete={toggleComplete}
                              onToggleFlag={toggleFlag}
                              onClick={handleTaskClick}
                              isSelectionMode={isSelectionMode}
                              isSelected={selectedTaskIds.has(item.task.id as number)}
                              onSelect={toggleTaskSelection}
                              onDelete={handleDeleteTask}
                              sortable={false}
                              taskGroups={taskGroupMap.get(item.task.id as number)}
                              onEnterSelectionMode={toggleSelectionMode}
                              viewMode={viewMode}
                              activeCategoryId={activeCategoryId}
                              isFocused={item.task.id === focusedTaskId}
                            />
                          )
                        } else {
                          return (
                            <SharedTaskInlineItem
                              key={item.task.id}
                              task={item.task}
                              onToggle={() => {
                                if (item.task.groupId && item.task.sharedTask) {
                                  toggleSharedTask(item.task.groupId, item.task.sharedTask.id, item.task.sharedTask.status)
                                }
                              }}
                              onClick={() => {
                                setSelectedSharedTask(item.task)
                              }}
                            />
                          )
                        }
                      })}
                      </AnimatePresence>
                    </TaskGroupSection>
                  ))
                }

                // ── 분리 뷰: 개인만 / 공유만 ──
                const personalGroupKeys = new Set(taskGroups.map(g => g.key))
                const sharedOnlyKeys = Object.keys(sharedTasksByGroupKey).filter(k => !personalGroupKeys.has(k))

                // 개인 그룹 렌더링
                const personalGroupElements = taskGroups.map((group) => {
                  return (
                    <TaskGroupSection
                      key={group.key}
                      label={group.label}
                      count={group.tasks.length}
                      color={group.color}
                      defaultOpen={group.key !== 'completed'}
                      viewMode={viewMode}
                    >
                      <AnimatePresence mode="popLayout" initial={false}>
                      {group.tasks.map((task) => {
                        const { name, color } = getCategoryInfo(task.categoryId)
                        return (
                          <TaskItem
                            key={task.id}
                            task={task}
                            categoryName={name}
                            categoryColor={color}
                            onToggleComplete={toggleComplete}
                            onToggleFlag={toggleFlag}
                            onClick={handleTaskClick}
                            isSelectionMode={isSelectionMode}
                            isSelected={selectedTaskIds.has(task.id as number)}
                            onSelect={toggleTaskSelection}
                            onDelete={handleDeleteTask}
                            sortable={isDesktop && task.status === 'pending' && viewMode !== 'card'}
                            taskGroups={taskGroupMap.get(task.id as number)}
                            onEnterSelectionMode={toggleSelectionMode}
                            viewMode={viewMode}
                            activeCategoryId={activeCategoryId}
                            isFocused={task.id === focusedTaskId}
                          />
                        )
                      })}
                      </AnimatePresence>
                    </TaskGroupSection>
                  )
                })

                // 공유 태스크만 있는 그룹 렌더링
                const sharedOnlyElements = (GROUP_ORDER as readonly string[])
                  .filter(k => sharedOnlyKeys.includes(k))
                  .map((key) => {
                    const sharedTasks = sharedTasksByGroupKey[key] || []
                    const meta = GROUP_META[key as keyof typeof GROUP_META] || { label: key }
                    return (
                      <TaskGroupSection
                        key={`shared-${key}`}
                        label={meta.label}
                        count={sharedTasks.length}
                        color={meta.color}
                        defaultOpen={key !== 'completed'}
                        viewMode={viewMode}
                      >
                        {sharedTasks.map((st) => (
                          <SharedTaskInlineItem
                            key={st.id}
                            task={st}
                            onToggle={() => {
                              if (st.groupId && st.sharedTask) {
                                toggleSharedTask(st.groupId, st.sharedTask.id, st.sharedTask.status)
                              }
                            }}
                            onClick={() => {
                              setSelectedSharedTask(st)
                            }}
                          />
                        ))}
                      </TaskGroupSection>
                    )
                  })

                return [...personalGroupElements, ...sharedOnlyElements]
              })()}
            </div>
          </SortableContext>
        </DndContext>
      )}
        </motion.div>
      )}
      </AnimatePresence>

      {/* Batch action bar */}
      {isSelectionMode && <BatchActionBar />}

      {/* Create modal */}
      <TaskCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
      </motion.div>{/* end pull-to-refresh wrapper */}
      </div>
      {selectedTaskId != null && (
        <TaskDetailPanel taskId={selectedTaskId} onClose={() => setSelectedTaskId(null)} />
      )}

      {/* 공유 태스크 상세 패널 (데스크톱) */}
      {selectedSharedTask?.sharedTask && selectedSharedTask.groupId && (
        <SharedTaskDetailPanel
          task={selectedSharedTask}
          onClose={() => setSelectedSharedTask(null)}
        />
      )}

      {/* 채팅/AI요약 패널 (2xl 데스크톱) */}
      {isAuxPanelOpen && <AuxPanel />}
      </div>
    </PageContainer>
  )
}
